import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';

const ContactForm = () => {
  const { language, t } = useLanguage();
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '' // Honeypot
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const payload = {
        "Formular": "Kontaktformular",
        "Navn": formData.name || '',
        "E-mail": formData.email || '',
        "Telefon": formData.phone || 'Ikke oplyst',
        "Besked": formData.message || '',
        "Indsendt fra sprog": language === 'da' ? 'Dansk side' : 'Engelsk side',
        website_url: formData.website_url || ''
      };

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '', website_url: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (status === 'success') {
    return (
      <div className="text-center py-12 px-6 flex flex-col items-center gap-4 animate-in fade-in zoom-in">
        <CheckCircle2 size={48} className="text-[#99A18C]" />
        <h3 className="text-2xl font-serif font-bold text-slate-900">{t('form_success_title')}</h3>
        <p className="text-slate-600">
          {t('form_success_desc')}
        </p>
        <button 
          onClick={() => setStatus('idle')}
          className="mt-4 px-6 py-2 bg-[#99A18C] text-white rounded-full font-semibold hover:bg-[#8a917e] transition-all cursor-pointer"
        >
          {language === 'da' ? 'Send en ny besked' : 'Send a new message'}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {/* Honeypot field - HIDDEN FROM HUMANS */}
      <div className="sr-only absolute -top-[9999px] left-[-9999px]">
        <label htmlFor="website_url">
          {language === 'da' ? 'Hvis du ser dette, så lad det stå tomt' : 'If you see this, leave it blank'}
        </label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          autoComplete="off"
        />
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="name" className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">{t('form_name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder={language === 'da' ? 'Dit navn' : 'Your name'}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#99A18C]/20 focus:border-[#99A18C] transition-all"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">{t('form_email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder={language === 'da' ? 'din@email.dk' : 'your@email.com'}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#99A18C]/20 focus:border-[#99A18C] transition-all"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="phone" className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">{t('form_phone')}</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={language === 'da' ? 'Dit telefonnummer' : 'Your phone number'}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#99A18C]/20 focus:border-[#99A18C] transition-all"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="message" className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">{t('form_message')}</label>
          <textarea
            id="message"
            name="message"
            required
            rows="5"
            value={formData.message}
            onChange={handleChange}
            placeholder={language === 'da' ? 'Skriv din besked her...' : 'Write your message here...'}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#99A18C]/20 focus:border-[#99A18C] transition-all resize-none"
          ></textarea>
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full bg-[#99A18C] hover:bg-[#8a917e] text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg hover:shadow-[#99A18C]/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
        >
          {status === 'loading' ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{t('ui_loading')}</span>
            </>
          ) : (
            <>
              <Send size={18} />
              <span>{t('ui_send_message')}</span>
            </>
          )}
        </button>

        {status === 'error' && (
          <p className="text-sm text-red-500 text-center font-medium animate-in fade-in">
            {t('form_error_desc')}
          </p>
        )}
      </div>
    </form>
  );
};

export default ContactForm;
